import React, { useState, useEffect } from 'react';
import { X, User, Edit2, Loader, Trash2, Star, ExternalLink } from 'lucide-react';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from './firebase';

const ProfileModal = ({ onClose, onEditWord, lang, t }) => {
    const [myWords, setMyWords] = useState([]);
    const [likedWords, setLikedWords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('myWords');

    useEffect(() => {
        const fetchProfileData = async () => {
            if (!auth.currentUser) return;
            setLoading(true);
            try {
                const email = auth.currentUser.email;

                // Fetch My Words
                const myQ = query(collection(db, "words"), where("authorEmail", "==", email));
                const myDocs = await getDocs(myQ);
                setMyWords(myDocs.docs.map(d => ({ id: d.id, ...d.data() })));

                // Fetch Liked Words
                const likedQ = query(collection(db, "words"), where("likes", "array-contains", email));
                const likedDocs = await getDocs(likedQ);
                setLikedWords(likedDocs.docs.map(d => ({ id: d.id, ...d.data() })));

            } catch (error) {
                console.error("Error fetching profile data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, []);

    const handleDelete = async (wordId, wordText) => {
        if (window.confirm(`Are you sure you want to delete "${wordText}"?`)) {
            try {
                await deleteDoc(doc(db, "words", wordId));
                setMyWords(myWords.filter(w => w.id !== wordId));
                // If it is also in liked words, remove it there too
                setLikedWords(likedWords.filter(w => w.id !== wordId));
            } catch (error) {
                console.error("Error deleting word:", error);
                alert("Failed to delete word.");
            }
        }
    };
    
    const wordsToDisplay = activeTab === 'myWords' ? myWords : likedWords;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            ></div>

            {/* Modal Card */}
            <div className="relative bg-white border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-2xl max-h-[80vh] flex flex-col">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 bg-red-500 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-y-1 hover:shadow-none transition-all"
                >
                    <X size={20} className="text-white" strokeWidth={3} />
                </button>

                {/* Header */}
                <div className="mb-4 border-b-4 border-black pb-4">
                    <h2 className="text-3xl font-black uppercase flex items-center gap-3">
                        <User size={32} strokeWidth={3} />
                        {t?.profileTitle || 'My Profile'}
                    </h2>
                    <p className="font-bold text-gray-500 mt-2">
                        {t?.loggedInAs || 'Logged in as:'} <span className="text-black">{auth.currentUser?.email}</span>
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex flex-col sm:flex-row gap-2 mb-6">
                    <button 
                        onClick={() => setActiveTab('myWords')}
                        className={`flex-1 py-3 px-2 font-black uppercase border-2 border-black transition-all text-sm sm:text-base ${activeTab === 'myWords' ? 'bg-[#5CE1E6] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'bg-gray-100 hover:bg-gray-200'}`}
                    >
                        {t?.myWords || 'Eklediğim Kelimeler'} ({myWords.length})
                    </button>
                    <button 
                        onClick={() => setActiveTab('likedWords')}
                        className={`flex-1 py-3 px-2 font-black uppercase border-2 border-black transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${activeTab === 'likedWords' ? 'bg-[#FFDE59] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'bg-gray-100 hover:bg-gray-200'}`}
                    >
                        <Star size={18} strokeWidth={3} className={activeTab === 'likedWords' ? 'fill-black' : ''} /> {t?.likedWords || 'Beğendiklerim'} ({likedWords.length})
                    </button>
                </div>

                {/* Content */}
                <div className="flex-grow overflow-y-auto">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <Loader className="animate-spin" size={32} />
                        </div>
                    ) : wordsToDisplay.length === 0 ? (
                        <div className="p-8 text-center border-2 border-dashed border-gray-400 font-bold text-gray-400">
                            {activeTab === 'myWords' ? (t?.noContributions || "Henüz hiç kelime eklemediniz.") : (t?.noLikedWords || "Henüz hiç kelime beğenmediniz.")}
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {wordsToDisplay.map(word => (
                                <div key={word.id} className="bg-[#f0f0f0] p-4 border-2 border-black flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                                    <div className="w-full sm:w-auto">
                                        <h4 className="font-black text-xl uppercase break-words">{word.word}</h4>
                                        <p className="text-sm font-bold text-gray-500">
                                            {word.meanings?.length || 0} {t?.meaningsCount || 'anlam'}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2 w-full sm:w-auto mt-2 sm:mt-0 justify-end">
                                        <button
                                            onClick={() => window.open(`/?word=${encodeURIComponent(word.word)}`, '_blank')}
                                            className="p-2 bg-white border-2 border-black hover:bg-gray-200 transition-colors flex items-center justify-center"
                                            title="Sitede Görüntüle"
                                        >
                                            <ExternalLink size={20} strokeWidth={2.5} />
                                        </button>
                                        
                                        {/* Edit and Delete are only for author */}
                                        {word.authorEmail === auth.currentUser?.email && (
                                            <>
                                                <button
                                                    onClick={() => onEditWord(word)}
                                                    className="p-2 bg-[#5CE1E6] border-2 border-black hover:bg-[#4bc7cc] transition-colors flex items-center justify-center"
                                                    title="Düzenle"
                                                >
                                                    <Edit2 size={20} strokeWidth={2.5} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(word.id, word.word)}
                                                    className="p-2 bg-red-500 border-2 border-black text-white hover:bg-red-600 transition-colors flex items-center justify-center"
                                                    title="Sil"
                                                >
                                                    <Trash2 size={20} strokeWidth={2.5} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default ProfileModal;
